import { definition, items, manifestInfo, table, tablesOf } from '../lib/manifest';
import { godRolls, type RollGroup, type RollMode } from './godrolls';

/**
 * D2 Info (port of the app's D2Info): every collectable weapon and armour piece with where it drops, Bungie's
 * activities, DIM's community wish list (god rolls, read by godrolls.ts) and one item's perk columns and stats. Built
 * from the Bungie tables the site already keeps.
 */

export interface D2Item {
  hash: number; name: string; icon: string; type: string; tier: number; itemType: number; subType: number; damage: number;
  ammo: number; classType: number; source: string; mark: string; tierName: string; category: string; norm: string; normType: string; normSrc: string;
}
export interface D2Activity { hash: number; name: string; desc: string; type: string; destination: string; place: string; image: string; light: number; pvp: boolean; norm: string }
export interface Perk { hash: number; name: string; icon: string; desc: string; type: string; enhanced: boolean }
export interface PerkColumn { intrinsic: boolean; perks: Perk[] }
/** One group of the wish list: its note and the perks it allows in each column (any mix of them is the roll). */
export interface GodRoll { mode: RollMode; note: string; cols: Perk[][] }
export interface ItemDetail {
  hash: number; name: string; icon: string; screenshot: string; flavor: string; type: string; tier: string; tierType: number; damage: number;
  ammo: number; itemType: number; classType: number; columns: PerkColumn[]; stats: { name: string; value: number }[]; source: string; mark: string;
  rolls: GodRoll[]; altRolls: GodRoll[];
  fixed: boolean;   // its perks are set (no column rolls at random): a curated or older version, nothing to chase
}

export const ELEMENTS: Record<number, string> = { 1: 'Kinetic', 2: 'Arc', 3: 'Solar', 4: 'Void', 6: 'Stasis', 7: 'Strand' };
export const DAMAGE_COLORS: Record<number, string> = { 1: '#E8ECF2', 2: '#7AECF3', 3: '#F2721B', 4: '#B185DF', 6: '#4D88FF', 7: '#35E366' };
export const AMMO: Record<number, string> = { 1: 'Primary', 2: 'Special', 3: 'Heavy' };
export const CLASS_NAMES: Record<number, string> = { 0: 'Titan', 1: 'Hunter', 2: 'Warlock' };
export const CATEGORIES: [string, RegExp | null][] = [
  ['Vanguard', /vanguard|nightfall|strike|zavala|grandmaster|onslaught|ikora/],
  ['Crucible', /crucible|trials|iron banner|competitive|shaxx|saint-14|osiris|lord saladin/],
  ['Gambit', /gambit|drifter/],
  ['Dungeons & Raids', /raid|dungeon/],
  ['Seasonal / Other', null],
];

/** "The Riposte" -> "riposte": lower case, no leading "the", no spaces or punctuation. */
export const norm = (s: string) => s.toLowerCase().replace(/^\s*the\s+/, '').replace(/[^a-z0-9]/g, '');
const sourceName = (s: string) => s.replace(/^\s*Source:\s*/i, '').trim().replace(/\.+$/, '');
export function category(source: string) {
  const l = source.toLowerCase();
  for (const [name, rx] of CATEGORIES) if (rx && rx.test(l)) return name;
  return 'Seasonal / Other';
}

export interface D2Index { items: D2Item[]; byHash: Map<number, D2Item>; activities: D2Activity[] }
let indexP: Promise<D2Index> | undefined;

/** The item and activity indexes, built once per page load from the kept tables. */
export function d2Index(): Promise<D2Index> {
  return (indexP ??= (async () => {
    const [lite, t] = await Promise.all([items(), tablesOf('DestinyCollectibleDefinition', 'DestinyActivityDefinition', 'DestinyActivityTypeDefinition', 'DestinyDestinationDefinition', 'DestinyPlaceDefinition')]);
    const coll = t.DestinyCollectibleDefinition;
    const list: D2Item[] = [];
    for (const [h, d] of Object.entries(lite)) {
      if ((d.itemType !== 3 && d.itemType !== 2) || !d.name || !d.collectible) continue;
      const src = sourceName(coll[d.collectible]?.sourceString ?? '');
      list.push({
        hash: Number(h), name: d.name, icon: d.icon, type: d.type, tier: d.tier, itemType: d.itemType, subType: d.subType, damage: d.damage, ammo: d.ammo,
        classType: d.classType, source: src, mark: d.watermark, tierName: d.tierName, category: src ? category(src) : '', norm: norm(d.name), normType: norm(d.type), normSrc: norm(src),
      });
    }
    const nm = (tb: Record<string, any>, h: number) => tb[h]?.displayProperties?.name ?? '';
    const acts: D2Activity[] = [];
    for (const [h, d] of Object.entries<any>(t.DestinyActivityDefinition)) {
      const name = d.displayProperties?.name ?? '', desc = d.displayProperties?.description ?? '';
      if (!name || !desc) continue;
      acts.push({
        hash: Number(h), name, desc, type: nm(t.DestinyActivityTypeDefinition, d.activityTypeHash), destination: nm(t.DestinyDestinationDefinition, d.destinationHash),
        place: nm(t.DestinyPlaceDefinition, d.placeHash), image: d.pgcrImage ?? '', light: d.activityLightLevel ?? 0, pvp: !!d.isPvP, norm: norm(name),
      });
    }
    return { items: list, byHash: new Map(list.map((i) => [i.hash, i])), activities: acts };
  })());
}

/**
 * Any item by its number, for the links into D2 Info from the rest of the hub (Collections, Character, Vendors, Legend,
 * Xûr...). The index holds only weapons and armour that have a Collections entry, so a link to anything else (an emblem,
 * a shader, a ship, an ornament, or another version of a weapon: a reissue, a world drop) found nothing and D2 Info
 * opened on its front page. Now: the index's own entry; else the item itself, built from Bungie's item data, with the
 * source of a namesake in the index when it has none (so the page still says where to get it).
 */
export async function itemByHash(ix: D2Index, hash: number): Promise<D2Item | undefined> {
  const hit = ix.byHash.get(hash);
  if (hit) return hit;
  const [lite, t] = await Promise.all([items(), tablesOf('DestinyCollectibleDefinition')]);
  const d = lite[hash];
  if (!d?.name) return undefined;
  const twin = ix.items.find((i) => i.norm === norm(d.name) && i.itemType === d.itemType);
  const src = sourceName(t.DestinyCollectibleDefinition[d.collectible]?.sourceString ?? '') || twin?.source || '';
  return {
    hash, name: d.name, icon: d.icon, type: d.type, tier: d.tier, itemType: d.itemType, subType: d.subType, damage: d.damage, ammo: d.ammo,
    classType: d.classType, source: src, mark: d.watermark, tierName: d.tierName, category: src ? category(src) : '', norm: norm(d.name), normType: norm(d.type), normSrc: norm(src),
  };
}

const actCache = new Map<string, D2Activity[]>();
/** Bungie's source text -> the activity: drop quotes and words like raid / dungeon. */
export function findActivity(ix: D2Index, source: string): D2Activity[] {
  const core = source.replace(/"/g, '').replace(/\b(raid|dungeon|exotic mission|mission)\b/gi, '').replace(/^[ .,]+|[ .,]+$/g, '');
  const cn = norm(core);
  if (cn.length < 4) return [];
  let list = actCache.get(cn);
  if (!list) actCache.set(cn, (list = ix.activities.filter((a) => a.norm === cn || (cn.length >= 6 && a.norm.startsWith(cn)))));
  return list;
}

export function categoryImage(ix: D2Index, cat: string) {
  const rx = { Vanguard: /Nightfall|Strike|Vanguard/, Crucible: /Crucible|Control|Clash|Trials|Iron Banner|Rift|Survival/, Gambit: /Gambit/, 'Dungeons & Raids': /Raid|Dungeon/ }[cat] ?? /Seasonal|Exotic Mission|Story|Event/;
  const a = ix.activities.filter((x) => x.image && rx.test(x.type));
  return a.length ? a[a.length >> 1].image : undefined;
}

// ---------------- one item: perk columns, stats, community rolls ----------------

export async function loadItem(ix: D2Index, it: D2Item): Promise<ItemDetail> {
  const [item, lite, stats] = await Promise.all([definition<any>('DestinyInventoryItemDefinition', it.hash), items(), table<any>('DestinyStatDefinition')]);
  if (!item) throw new Error('That item is not in the database.');
  const perkIdx: number[] = [], intrIdx: number[] = [];
  for (const c of item.sockets?.socketCategories ?? []) {
    if (c.socketCategoryHash === 4241085061) perkIdx.push(...c.socketIndexes);
    if (c.socketCategoryHash === 3956125808) intrIdx.push(...c.socketIndexes);
  }
  const entries: any[] = item.sockets?.socketEntries ?? [];
  const plugSet = (e: any): number => e?.randomizedPlugSetHash || e?.reusablePlugSetHash || 0;
  const order = [...intrIdx, ...perkIdx];
  const sets = new Map<number, number[]>();
  await Promise.all(order.map(async (i) => {
    const ps = plugSet(entries[i]);
    if (!ps || sets.has(ps)) return;
    sets.set(ps, []);
    const d = await definition<any>('DestinyPlugSetDefinition', ps);
    sets.set(ps, [...new Set<number>((d?.reusablePlugItems ?? []).filter((p: any) => p.currentlyCanRoll !== false).map((p: any) => p.plugItemHash))]);
  }));
  const plug = (h: number): Perk | null => {
    const p = lite[h];
    if (!p?.name) return null;
    return { hash: h, name: p.name, icon: p.icon, desc: p.desc, type: p.type, enhanced: p.type.includes('Enhanced') };
  };
  const columns: PerkColumn[] = [];
  for (const i of order) {
    const e = entries[i];
    if (!e) continue;
    const ps = plugSet(e);
    const hashes = ps ? sets.get(ps) ?? [] : e.singleInitialItemHash ? [e.singleInitialItemHash] : [];
    const seen = new Set<string>();
    let perks: Perk[] = [];
    for (const h of hashes) { const p = plug(h); if (p && !seen.has(p.name)) { seen.add(p.name); perks.push(p); } }
    perks = [...perks.filter((p) => !p.enhanced), ...perks.filter((p) => p.enhanced)];
    if (perks.length) columns.push({ intrinsic: intrIdx.includes(i), perks });
  }
  const statList: { name: string; value: number }[] = [];
  for (const [sh, sv] of Object.entries<any>(item.stats?.stats ?? {})) {
    const name = stats[sh]?.displayProperties?.name ?? '';
    if (name && sv.value > 0) statList.push({ name, value: sv.value });
  }
  // the community rolls, one per wish-list group: each column's perks by name (the plain one before the enhanced one),
  // the same group listed twice kept once
  const wish = await godRolls();
  const rollsOf = (groups: RollGroup[]): GodRoll[] => {
    const seen = new Set<string>();
    const out: GodRoll[] = [];
    for (const g of groups) {
      const cols = g.cols.map((c) => {
        const names = new Set<string>();
        return c.map(plug).filter((p): p is Perk => !!p).sort((a, b) => +a.enhanced - +b.enhanced).filter((p) => !names.has(p.name) && !!names.add(p.name));
      }).filter((c) => c.length);
      const k = `${g.mode}|${g.note}|${cols.map((c) => c.map((p) => p.name).join(',')).join('|')}`;
      if (cols.length && !seen.has(k)) { seen.add(k); out.push({ mode: g.mode, note: g.note, cols }); }
    }
    return out;
  };
  const rolls = rollsOf(wish[it.hash] ?? []);
  // no community roll for this exact version? use a reissue with the same name (the list covers current versions)
  let altRolls: GodRoll[] = [];
  if (!rolls.length) {
    const other = ix.items.filter((x) => x.norm === it.norm && x.hash !== it.hash).map((x) => wish[x.hash] ?? []).sort((a, b) => b.length - a.length)[0];
    if (other?.length) altRolls = rollsOf(other);
  }
  return {
    hash: it.hash, name: item.displayProperties?.name ?? it.name, icon: item.displayProperties?.icon ?? '', screenshot: item.screenshot ?? '', flavor: item.flavorText ?? '',
    type: item.itemTypeDisplayName ?? '', tier: item.inventory?.tierTypeName ?? '', tierType: item.inventory?.tierType ?? 0, damage: item.defaultDamageType ?? 0,
    ammo: item.equippingBlock?.ammoType ?? 0, itemType: item.itemType ?? 0, classType: item.classType ?? 3, columns, stats: statList, source: it.source,
    mark: item.iconWatermark ?? '', rolls, altRolls, fixed: perkIdx.length > 0 && perkIdx.every((i) => !entries[i]?.randomizedPlugSetHash),
  };
}

// ---------------- raid / dungeon guide: the Destiny Wiki article ----------------

export interface Guide { name: string; title: string; url: string; blocks: { kind: string; text: string }[] }
const guides = new Map<string, Promise<Guide>>();

export function guide(name: string): Promise<Guide> {
  let p = guides.get(name);
  if (!p) {
    p = (async () => {
      const api = 'https://destiny.fandom.com/api.php';
      const os = await (await fetch(`${api}?action=opensearch&search=${encodeURIComponent(name)}&limit=5&namespace=0&format=json&origin=*`)).json();
      const title: string | undefined = (os?.[1] ?? []).find((t: string) => t && !/\((gallery|lore|disambiguation)\)/i.test(t));
      if (!title) throw new Error(`The Destiny Wiki has no article for ${name}.`);
      const pr = await (await fetch(`${api}?action=parse&page=${encodeURIComponent(title)}&prop=text&format=json&redirects=1&disablelimitreport=1&origin=*`)).json();
      const doc = new DOMParser().parseFromString(pr?.parse?.text?.['*'] ?? '', 'text/html');
      doc.querySelectorAll('table, aside, sup, script, style, figure, .toc, .reference, .gallery, .wikia-gallery, .mw-editsection').forEach((e) => e.remove());
      const blocks: Guide['blocks'] = [];
      for (const el of doc.querySelectorAll('h2, h3, h4, p, li')) {
        const text = (el.textContent ?? '').replace(/\[edit\]/g, '').replace(/\s+/g, ' ').trim();
        if (!text) continue;
        const kind = el.tagName.toLowerCase();
        if (kind === 'h2' && /^(References|Gallery|External links|Trivia|Lore|Navigation|See also|List of appearances)/.test(text)) break;
        blocks.push({ kind, text });
        if (blocks.length >= 320) break;
      }
      return { name, title, url: 'https://destiny.fandom.com/wiki/' + encodeURIComponent(title.replace(/ /g, '_')), blocks };
    })();
    p.catch(() => guides.delete(name));
    guides.set(name, p);
  }
  return p;
}

export const manifestVersion = () => manifestInfo().then((m) => m.version);
